import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { RowSelectionState, DropdownItem } from 'design-system-web';
import { bulkAssignTaskTeamsMutation } from 'graphql-queries';
import { Dropdown } from 'components';
import { AdminUser } from 'packages/lane-shared/domains/workOrder/hooks/useAssignableMembers';

const TASK_SELECTION_LIMIT = 100;

type Props = {
    rowSelection?: RowSelectionState;
    assignableTeams: AdminUser[];
    refetch?: () => void;
};

export function BulkTeamsAssignAction({ rowSelection, assignableTeams, refetch }: Props) {
    const { t } = useTranslation();
    const [bulkAssignTaskTeams] = useMutation(bulkAssignTaskTeamsMutation);

    const handleTeamsAssign = async (teams: AdminUser[]) => {
        if (!teams || teams.length === 0 || !rowSelection) return;

        const selectedTaskIds = Object.entries(rowSelection)
            .filter(([_, isSelected]) => isSelected)
            .map(([taskId]) => taskId);

        if (selectedTaskIds.length === 0) {
            await window.Toast.show(
                t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignTeams.noSelection')
            );
            return;
        }

        if (selectedTaskIds.length > TASK_SELECTION_LIMIT) {
            await window.Toast.show(
                t(
                    'web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignTeams.limitExceeded',
                    {
                        taskSelectionLimit: TASK_SELECTION_LIMIT,
                        selectedTaskCount: selectedTaskIds.length,
                    }
                ),
                5000
            );
            return;
        }

        try {
            await window.Alert.confirm({
                title: t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignTeams.confirm.title'),
                message: t(
                    'web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignTeams.confirm.message',
                    {
                        count: selectedTaskIds.length,
                        teamCount: 1,
                    }
                ),
                confirmText: t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignTeams.confirm.button'),
                cancelText: t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignTeams.cancel.button'),
            });
        } catch {
            // User cancelled - no state to clean up
            return;
        }

        window.Alert.loading({
            title: t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignTeams.inProgress'),
            disableCloseOnBackgroundClick: true,
        });

        try {
            const assigneeGroups = teams.map((team: AdminUser) => team.value);

            const result = await bulkAssignTaskTeams({
                variables: {
                    taskIds: selectedTaskIds,
                    assigneeGroups,
                },
            });

            const { successCount, failureCount } =
                result.data?.bulkAssignTaskTeams || {
                    successCount: 0,
                    failureCount: selectedTaskIds.length,
                };

            window.Alert.hide();

            if (failureCount === 0) {
                await window.Toast.show(
                    t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignTeams.success', {
                        count: successCount,
                    })
                );
            } else if (successCount === 0) {
                await window.Toast.show(
                    t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignTeams.error')
                );
            } else {
                await window.Toast.show(
                    t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignTeams.partial', {
                        successCount,
                        failureCount,
                    }),
                    5000
                );
            }

            if (refetch) {
                refetch();
            }
        } catch {
            window.Alert.hide();
            await window.Toast.show(
                t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignTeams.error')
            );
        }
    };

    return (
        <div style={{ minWidth: '200px' }}>
            <Dropdown
                isSearchable
                value=""
                items={assignableTeams || []}
                onChange={(item: DropdownItem<string>) => {
                    if (item?.value) {
                        const selectedTeam = assignableTeams.find(team => team.value === item.value);
                        if (selectedTeam) {
                            handleTeamsAssign([selectedTeam]);
                        }
                    }
                }}
                placeholder={t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignTeams.selectTeams')}
                doTranslation={false}
                testId="bulk-task-teams-assign-select"
            />
        </div>
    );
}
